<template>
  <div>
    <div class="d-flex align-items-center mb-3">
      <div class="flex-fill">
        <h3 class="mb-0">
          {{
            $t(
              type === "development"
                ? "global.development-environments"
                : "global.staging-environments"
            )
          }}
        </h3>
      </div>
      <ActionIcons v-if="showActions()" :actions="actions" />
    </div>
    <template v-if="sites.length > 0">
      <div
        v-for="(item, index) in sites"
        :key="item.id"
        :class="{ 'mb-4': index < sites.length - 1 }"
      >
        <template v-if="item.isStable">
          <div>
            <router-link :to="getRouterLink(item)">
              {{ item.domain }}
            </router-link>
          </div>
          <div>
            <span class="mr-2">{{ item.ip }}</span>
            <CopyToClipboard :text="item.ip" />
          </div>
        </template>
        <span v-else>{{ item.domain }}</span>

        <div class="mt-2">
          <div v-if="item.isStable" class="d-flex">
            <Btn @click="openEnvironment(item)" variant="primary" class="mr-2">
              <Icon icon="open-page" color="white" class="mr-2" />
              {{ $t("global.view") }}
            </Btn>
            <div
              @mouseover="setSupportHover(item.id, true)"
              @mouseleave="setSupportHover(item.id, false)"
            >
              <Btn @click="openSupport(item)" variant="primaryOutline" class="mr-2">
                <Icon
                  icon="email"
                  :color="isSupportHovering(item.id) ? 'white' : 'gamma'"
                  class="mr-2"
                />
                {{ $t("global.support") }}
              </Btn>
            </div>
            <div
              @mouseover="setDeleteHover(item.id, true)"
              @mouseleave="setDeleteHover(item.id, false)"
            >
              <Btn @click="openDelete(item)" variant="dangerOutline">
                <Icon
                  icon="trash"
                  :color="isDeleteHovering(item.id) ? 'white' : 'tau'"
                  class="mr-2"
                />
                {{ $t("global.delete") }}
              </Btn>
            </div>
          </div>
          <div v-else class="d-flex justify-content-center align-items-center">
            <div class="mr-2">
              <LoadingState :size="iconSize" />
            </div>
            <div class="text-capitalize">{{ item.state }}...</div>
          </div>
        </div>
      </div>
    </template>
    <div v-else>
      {{
        $t(
          type === "development"
            ? "global.no-development-environments"
            : "global.no-staging-environments"
        )
      }}
    </div>
  </div>
</template>

<script>
import ActionIcons from "@/components/display/ActionIcons";
import Btn from "@/components/display/Btn";
import constants from "@/utils/constants";
import ContactSupport from "@/components/smart/modals/ContactSupport";
import CopyToClipboard from "@/components/display/CopyToClipboard";
import DevSiteAddModal from "@/components/smart/modals/DevSiteAdd";
import DevSiteDeleteModal from "@/components/smart/modals/DevSiteDelete";
import FreeTrialPayWallModal from "@/components/smart/modals/FreeTrialPayWall";
import Icon from "@/components/display/icon/Icon";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import { ref } from "@vue/composition-api";
import store from "@/store";

export default {
  name: "DevEnvironmentsTypeList",
  components: {
    ActionIcons,
    Btn,
    CopyToClipboard,
    Icon,
    LoadingState,
  },
  props: {
    plan: {
      type: Object,
      require: true,
    },
    site: {
      type: Object,
      require: true,
    },
    sites: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: "development",
      validator: (val) => ["development", "staging"].includes(val),
    },
  },
  setup(props, { emit, root }) {
    const deleteHover = ref([]);
    const supportHover = ref([]);

    const actions = [
      new NavLink(
        root.$t("global.add-dev-environment"),
        "add",
        () => create(),
        false,
        null,
        "epsilon"
      ),
    ];

    const create = () => {
      if (!props.plan.package.isFreeTrial || props.type === "staging") {
        store.dispatch("modals/open", {
          template: DevSiteAddModal,
          data: { site: props.site, plan: props.plan, environmentType: props.type },
        });
      } else {
        store.dispatch("modals/open", {
          template: FreeTrialPayWallModal,
          data: {
            package: props.plan.package,
            app: props.site.app,
            plan: props.plan,
          },
        });
      }
      emit("hide");
    };

    const getRouterLink = (site) => ({
      name: "plan-site-dev-detail",
      params: {
        planId: props.plan.id,
        siteId: props.site.id,
        devSiteId: site.id,
      },
    });

    const iconSize = constants.TABLE_ICON_SIZE;

    const isDeleteHovering = (id) => deleteHover.value.indexOf(id) > -1;
    const isSupportHovering = (id) => supportHover.value.indexOf(id) > -1;

    const openDelete = (site) => {
      store.dispatch("modals/open", {
        template: DevSiteDeleteModal,
        data: { domain: site.domain, environmentType: site.environmentType, planId: site.plan.id },
      });
      emit("hide");
    };

    const openEnvironment = (site) => root.$router.push(getRouterLink(site));

    const openSupport = (site) => {
      store.dispatch("modals/open", {
        template: ContactSupport,
        data: { serviceId: site.plan.id, department: root.$t("global.support") },
      });
      emit("hide");
    };

    const setDeleteHover = (id, value) => {
      const exists = deleteHover.value.find((item) => item === id);
      if (value && !exists) {
        deleteHover.value.push(id);
      } else if (!value && exists) {
        const index = deleteHover.value.indexOf(id);
        if (index > -1) deleteHover.value.splice(index, 1);
      }
    };

    const setSupportHover = (id, value) => {
      const exists = supportHover.value.find((item) => item === id);
      if (value && !exists) {
        supportHover.value.push(id);
      } else if (!value && exists) {
        const index = supportHover.value.indexOf(id);
        if (index > -1) supportHover.value.splice(index, 1);
      }
    };

    const showActions = () => props.type === "development" || props.sites.length === 0;

    return {
      actions,
      getRouterLink,
      iconSize,
      isDeleteHovering,
      isSupportHovering,
      openDelete,
      openEnvironment,
      openSupport,
      setDeleteHover,
      setSupportHover,
      showActions,
    };
  },
};
</script>
